import { CSSProperties, ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../shared/design/Icons';
import { TMLogo } from '../shared/design/Primitives';

type NavId = 'home' | 'scan' | 'compare' | 'leaderboard' | 'about';

const NAV_ITEMS: { id: NavId; label: string; icon: JSX.Element; to: string }[] = [
  { id: 'home', label: 'Home', icon: <Icon.Home />, to: '/' },
  { id: 'scan', label: 'Scan replay', icon: <Icon.Scan />, to: '/scan' },
  { id: 'compare', label: 'Compare', icon: <Icon.Compare />, to: '/compare' },
  { id: 'leaderboard', label: 'Leaderboard', icon: <Icon.Leaderboard />, to: '/leaderboard' },
  { id: 'about', label: 'About', icon: <Icon.About />, to: '/about' },
];

function deriveActive(pathname: string): NavId {
  if (pathname.startsWith('/scan')) return 'scan';
  if (pathname.startsWith('/compare')) return 'compare';
  if (pathname.startsWith('/leaderboard')) return 'leaderboard';
  if (pathname.startsWith('/about')) return 'about';
  return 'home';
}

export function LeftRail({ active, compact = false }: { active?: NavId; compact?: boolean }) {
  const { pathname } = useLocation();
  const current = active ?? deriveActive(pathname);

  return (
    <nav
      style={{
        width: compact ? 60 : 220,
        background: 'var(--bg-rail)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <div style={{ padding: compact ? '20px 12px' : '20px 18px', borderBottom: '1px solid var(--border)' }}>
        {compact ? (
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <rect x="0" y="0" width="32" height="32" rx="6" fill="var(--accent)" />
            <path d="M6 11 L 16 21 L 26 11 L 22 11 L 16 17 L 10 11 Z" fill="#051019" />
            <rect x="6" y="22" width="20" height="2.5" fill="#051019" opacity="0.85" />
          </svg>
        ) : (
          <TMLogo size={28} />
        )}
      </div>
      <div style={{ padding: compact ? '12px 8px' : '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map((it) => {
          const isActive = current === it.id;
          return (
            <Link
              key={it.id}
              to={it.to}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: compact ? '10px' : '9px 12px',
                justifyContent: compact ? 'center' : 'flex-start',
                background: isActive ? 'var(--accent-12)' : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--fg-2)',
                border: 'none',
                borderRadius: 'var(--r-6)',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                textAlign: 'left',
                textDecoration: 'none',
                position: 'relative',
              }}
            >
              {isActive && (
                <span
                  style={{
                    position: 'absolute',
                    left: -10,
                    top: 6,
                    bottom: 6,
                    width: 2,
                    background: 'var(--accent)',
                    borderRadius: 1,
                  }}
                />
              )}
              {it.icon}
              {!compact && <span>{it.label}</span>}
            </Link>
          );
        })}
      </div>
      <div style={{ flex: 1 }} />
      {!compact && (
        <div
          style={{
            padding: '14px 18px',
            borderTop: '1px solid var(--border)',
            fontSize: 11,
            color: 'var(--fg-muted)',
            lineHeight: 1.5,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            v0.4.2
          </div>
          <div>
            Created by Proxo
            <br />& SindreMA
          </div>
        </div>
      )}
    </nav>
  );
}

export function TopBar({
  search = true,
  placeholder = 'Paste replay URL or upload .Replay.gbx',
}: {
  search?: boolean;
  placeholder?: string;
}) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');

  const submit = (raw: string) => {
    const url = raw.trim();
    if (!url) return;
    setValue('');
    navigate(`/scan?url=${encodeURIComponent(url)}`);
  };

  return (
    <div
      style={{
        height: 56,
        background: 'var(--bg-header)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 20px',
        flexShrink: 0,
      }}
    >
      {search && (
        <form
          onSubmit={(e) => { e.preventDefault(); submit(value); }}
          style={{
            flex: 1,
            maxWidth: 640,
            height: 36,
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-6)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            color: 'var(--fg-3)',
          }}
        >
          <Icon.Search />
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--fg-1)',
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
            }}
          />
          <span
            style={{
              fontSize: 10,
              color: 'var(--fg-muted)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              padding: '1px 6px',
              fontFamily: 'var(--font-mono)',
            }}
          >
            ⏎
          </span>
        </form>
      )}
      <div style={{ flex: 1 }} />
      <button
        style={{
          height: 30,
          padding: '0 10px',
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-6)',
          color: 'var(--fg-2)',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
      >
        <Icon.Settings /> Settings
      </button>
    </div>
  );
}

export function PageShell({
  active,
  children,
  search = true,
  topBarPlaceholder,
  style,
}: {
  active?: NavId;
  children?: ReactNode;
  search?: boolean;
  topBarPlaceholder?: string;
  style?: CSSProperties;
}) {
  return (
    <div className="tm" style={{ display: 'flex', height: '100%', width: '100%', background: 'var(--bg-page)', ...style }}>
      <LeftRail active={active} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar search={search} placeholder={topBarPlaceholder} />
        <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}
